import React, { useState, useEffect } from 'react';
import {
   Typography,
   Box,
   Paper,
   Grid,
   Button,
   Chip,
   Divider,
   Card,
   CardContent,
   CardMedia,
   CardActions,
   styled,
   Dialog,
   DialogTitle,
   DialogContent,
   IconButton,
   Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeIcon from '@mui/icons-material/QrCode';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

// Using require syntax to avoid ES module import issues
const QRCode = require('qrcode.react').QRCodeSVG;

// Styled components
const PageContainer = styled(Box)(({ theme }) => ({
   padding: theme.spacing(3),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
   marginBottom: theme.spacing(3),
   fontWeight: 600,
}));

const EmptyState = styled(Paper)(({ theme }) => ({
   padding: theme.spacing(4),
   textAlign: 'center',
   marginBottom: theme.spacing(2),
}));

const TicketCard = styled(Card)(({ theme }) => ({
   height: '100%',
   display: 'flex',
   flexDirection: 'column',
   transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
   overflow: 'visible',
   position: 'relative',
   '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[6],
   },
}));

const TicketBanner = styled(Box)(({ theme }) => ({
   position: 'absolute',
   top: -10,
   right: 20,
   backgroundColor: theme.palette.primary.main,
   color: theme.palette.primary.contrastText,
   padding: theme.spacing(0.5, 2),
   borderRadius: '4px',
   fontWeight: 'bold',
   zIndex: 1,
   boxShadow: theme.shadows[2],
}));

const TicketDetail = styled(Box)(({ theme }) => ({
   display: 'flex',
   alignItems: 'center',
   marginBottom: theme.spacing(1),
}));

const QRCodeContainer = styled(Box)(({ theme }) => ({
   display: 'flex',
   flexDirection: 'column',
   alignItems: 'center',
   justifyContent: 'center',
   padding: theme.spacing(4),
}));

const TicketInfo = styled(Box)(({ theme }) => ({
   marginTop: theme.spacing(2),
   marginBottom: theme.spacing(2),
}));

const InfoItem = styled(Box)(({ theme }) => ({
   display: 'flex',
   alignItems: 'center',
   marginBottom: theme.spacing(1),
}));

const OrgInfoContainer = styled(Box)(({ theme }) => ({
   display: 'flex',
   flexDirection: 'column',
   padding: theme.spacing(2),
   marginTop: theme.spacing(2),
   backgroundColor: theme.palette.background.default,
   borderRadius: theme.shape.borderRadius,
}));

const OrgAvatar = styled(Box)(({ theme }) => ({
   display: 'flex',
   alignItems: 'center',
   marginBottom: theme.spacing(1),
}));

// Mock data for tickets
interface TicketType {
   id: string;
   eventId: string;
   eventName: string;
   eventDate: Date;
   eventLocation: string;
   eventImage: string;
   organizationId: string;
   organizationName: string;
   organizationLogo: string;
   ticketType: string;
   ticketPrice: number;
   purchaseDate: Date;
   status: 'active' | 'used' | 'expired' | 'cancelled';
   attendees: string[];
   ticketCode: string;
}

// Mock data
const mockTickets: TicketType[] = [
   {
      id: 'ticket1',
      eventId: 'event1',
      eventName: 'Summer Hiking Adventure',
      eventDate: new Date(2025, 5, 15, 9, 0), // June 15, 2025, 9:00 AM
      eventLocation: 'Rocky Mountain National Park',
      eventImage: 'https://picsum.photos/seed/event1/600/400',
      organizationId: 'org1',
      organizationName: 'Adventure Seekers',
      organizationLogo: 'https://picsum.photos/seed/org1/200/200',
      ticketType: 'Standard Pass',
      ticketPrice: 25,
      purchaseDate: new Date(2025, 4, 10), // May 10, 2025
      status: 'active',
      attendees: ['John Doe'],
      ticketCode: 'ADV-HIKE-25-1234',
   },
   {
      id: 'ticket2',
      eventId: 'event2',
      eventName: 'Photography Workshop: Urban Landscapes',
      eventDate: new Date(2025, 5, 22, 14, 0), // June 22, 2025, 2:00 PM
      eventLocation: 'Downtown Art Center',
      eventImage: 'https://picsum.photos/seed/event2/600/400',
      organizationId: 'org3',
      organizationName: 'Photography Masters',
      organizationLogo: 'https://picsum.photos/seed/org3/200/200',
      ticketType: 'Workshop Pass',
      ticketPrice: 45,
      purchaseDate: new Date(2025, 5, 5), // June 5, 2025
      status: 'active',
      attendees: ['John Doe'],
      ticketCode: 'PHOTO-WORK-45-5678',
   },
   {
      id: 'ticket3',
      eventId: 'event3',
      eventName: 'Historical Walking Tour',
      eventDate: new Date(2025, 7, 5, 10, 0), // August 5, 2025, 10:00 AM
      eventLocation: 'Old Town District',
      eventImage: 'https://picsum.photos/seed/event3/600/400',
      organizationId: 'org5',
      organizationName: 'Cultural Heritage Tours',
      organizationLogo: 'https://picsum.photos/seed/org5/200/200',
      ticketType: 'Tour Pass',
      ticketPrice: 15,
      purchaseDate: new Date(2025, 6, 20), // July 20, 2025
      status: 'active',
      attendees: ['John Doe', 'Jane Doe'],
      ticketCode: 'CULTURAL-TOUR-15-9012',
   },
   {
      id: 'ticket4',
      eventId: 'event4',
      eventName: 'Food Festival Tasting Pass',
      eventDate: new Date(2025, 5, 30, 12, 0), // June 30, 2025, 12:00 PM
      eventLocation: 'City Food Hall',
      eventImage: 'https://picsum.photos/seed/event4/600/400',
      organizationId: 'org4',
      organizationName: 'Local Food Explorers',
      organizationLogo: 'https://picsum.photos/seed/org4/200/200',
      ticketType: 'Tasting Pass',
      ticketPrice: 35,
      purchaseDate: new Date(2025, 5, 15), // June 15, 2025
      status: 'active',
      attendees: ['John Doe'],
      ticketCode: 'FOOD-FEST-35-3456',
   },
];

const MyTicketsPage: React.FC = () => {
   const { currentUser } = useAuth();
   const [tickets, setTickets] = useState<TicketType[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState('');
   const [selectedTicket, setSelectedTicket] = useState<TicketType | null>(null);
   const [qrDialogOpen, setQrDialogOpen] = useState(false);

   useEffect(() => {
      // In a real app, we would fetch tickets from Firestore here
      // For now, use the mock data
      setTickets(mockTickets);
      setLoading(false);

      // Example of how you would fetch in a real app:
      /*
    const fetchTickets = async () => {
      try {
        setLoading(true);
        
        if (!currentUser) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }
        
        const userTicketsRef = collection(db, 'users', currentUser.uid, 'tickets');
        const snapshot = await getDocs(userTicketsRef);
        
        if (snapshot.empty) {
          setTickets([]);
          setLoading(false);
          return;
        }
        
        // Fetch full ticket details
        const ticketsWithDetails = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const ticket = { id: doc.id, ...doc.data() };
            // Get event details
            // Get organization details
            
            return ticket;
          })
        );
        
        setTickets(ticketsWithDetails);
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load your tickets. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser) {
      fetchTickets();
    }
    */
   }, [currentUser]);

   const handleShowQrCode = (ticket: TicketType) => {
      setSelectedTicket(ticket);
      setQrDialogOpen(true);
   };

   const handleCloseQrDialog = () => {
      setQrDialogOpen(false);
   };

   const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
         weekday: 'long',
         year: 'numeric',
         month: 'long',
         day: 'numeric',
      });
   };

   const formatTime = (date: Date) => {
      return date.toLocaleTimeString('en-US', {
         hour: '2-digit',
         minute: '2-digit',
      });
   };

   // Get status color
   const getStatusColor = (status: string) => {
      switch (status) {
         case 'active':
            return 'success';
         case 'used':
            return 'info';
         case 'expired':
            return 'warning';
         case 'cancelled':
            return 'error';
         default:
            return 'default';
      }
   };

   return (
      <PageContainer>
         <PageTitle variant='h4'>My Tickets</PageTitle>

         {loading ? (
            <EmptyState>
               <Typography>Loading your tickets...</Typography>
            </EmptyState>
         ) : error ? (
            <EmptyState>
               <Typography color='error'>{error}</Typography>
            </EmptyState>
         ) : tickets.length === 0 ? (
            <EmptyState>
               <Typography variant='h6'>You don't have any tickets yet</Typography>
               <Typography variant='body1' paragraph>
                  Explore upcoming events and purchase tickets to get started.
               </Typography>
               <Button variant='contained' color='primary' component={Link} to='/explore-events'>
                  Explore Events
               </Button>
            </EmptyState>
         ) : (
            <Grid container spacing={3}>
               {tickets.map((ticket) => (
                  <Grid item xs={12} md={6} lg={4} key={ticket.id}>
                     <TicketCard elevation={3}>
                        <TicketBanner>
                           <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ConfirmationNumberIcon sx={{ mr: 1 }} />
                              {ticket.ticketType}
                           </Box>
                        </TicketBanner>

                        <CardMedia
                           component='img'
                           height='160'
                           image={ticket.eventImage}
                           alt={ticket.eventName}
                        />

                        <CardContent>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box
                                 component='img'
                                 src={ticket.organizationLogo}
                                 alt={ticket.organizationName}
                                 sx={{ width: 30, height: 30, mr: 1, borderRadius: '50%' }}
                              />
                              <Typography variant='subtitle2' color='textSecondary'>
                                 {ticket.organizationName}
                              </Typography>
                           </Box>

                           <Typography variant='h6' gutterBottom>
                              {ticket.eventName}
                           </Typography>

                           <Chip
                              label={ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                              color={getStatusColor(ticket.status) as any}
                              size='small'
                              sx={{ mb: 2 }}
                           />

                           <TicketDetail>
                              <EventIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant='body2'>
                                 {formatDate(ticket.eventDate)}
                              </Typography>
                           </TicketDetail>

                           <TicketDetail>
                              <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant='body2'>
                                 {formatTime(ticket.eventDate)}
                              </Typography>
                           </TicketDetail>

                           <TicketDetail>
                              <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant='body2'>{ticket.eventLocation}</Typography>
                           </TicketDetail>

                           <Divider sx={{ my: 2 }} />

                           <Typography variant='subtitle2' gutterBottom>
                              Attendees:
                           </Typography>
                           <Box sx={{ mb: 1 }}>
                              {ticket.attendees.map((attendee, index) => (
                                 <Chip
                                    key={index}
                                    label={attendee}
                                    size='small'
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                 />
                              ))}
                           </Box>
                        </CardContent>

                        <Box sx={{ flexGrow: 1 }} />

                        <CardActions sx={{ p: 2, pt: 0, flexDirection: 'column' }}>
                           <Button
                              variant='outlined'
                              color='primary'
                              startIcon={<QrCodeIcon />}
                              onClick={() => handleShowQrCode(ticket)}
                              fullWidth
                              sx={{ mb: 1 }}
                           >
                              Show Ticket QR Code
                           </Button>

                           <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
                              <Button
                                 variant='text'
                                 color='primary'
                                 startIcon={<BusinessIcon />}
                                 component={Link}
                                 to={`/event-details/${ticket.organizationId}/${ticket.eventId}`}
                                 size='small'
                                 sx={{ flex: 1 }}
                              >
                                 Event Info
                              </Button>

                              <Button
                                 variant='text'
                                 color='primary'
                                 startIcon={<InfoIcon />}
                                 component={Link}
                                 to={`/explore-events/?organizationId=${ticket.organizationId}`}
                                 size='small'
                                 sx={{ flex: 1 }}
                              >
                                 More Events
                              </Button>
                           </Box>
                        </CardActions>
                     </TicketCard>
                  </Grid>
               ))}
            </Grid>
         )}

         {/* QR Code Dialog */}
         <Dialog open={qrDialogOpen} onClose={handleCloseQrDialog} maxWidth='sm' fullWidth>
            <DialogTitle>
               Ticket QR Code
               <IconButton
                  aria-label='close'
                  onClick={handleCloseQrDialog}
                  sx={{
                     position: 'absolute',
                     right: 8,
                     top: 8,
                  }}
               >
                  <CloseIcon />
               </IconButton>
            </DialogTitle>
            <DialogContent>
               {selectedTicket && (
                  <>
                     <QRCodeContainer>
                        <QRCode
                           value={selectedTicket.ticketCode}
                           size={200}
                           level='H'
                           includeMargin={true}
                        />
                        <Typography variant='h6' align='center' sx={{ mt: 2 }}>
                           {selectedTicket.ticketCode}
                        </Typography>
                     </QRCodeContainer>

                     <Divider sx={{ my: 2 }} />

                     <TicketInfo>
                        <Typography variant='h6' gutterBottom>
                           {selectedTicket.eventName}
                        </Typography>

                        <InfoItem>
                           <EventIcon sx={{ mr: 1, color: 'text.secondary' }} />
                           <Typography variant='body1'>
                              {formatDate(selectedTicket.eventDate)}
                           </Typography>
                        </InfoItem>

                        <InfoItem>
                           <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                           <Typography variant='body1'>
                              {formatTime(selectedTicket.eventDate)}
                           </Typography>
                        </InfoItem>

                        <InfoItem>
                           <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                           <Typography variant='body1'>{selectedTicket.eventLocation}</Typography>
                        </InfoItem>

                        <Box sx={{ mt: 2 }}>
                           <Typography variant='subtitle1'>
                              Ticket Type: {selectedTicket.ticketType}
                           </Typography>
                           <Typography variant='subtitle1'>
                              Price: ${selectedTicket.ticketPrice.toFixed(2)}
                           </Typography>
                           <Typography variant='subtitle1'>
                              Purchased: {formatDate(selectedTicket.purchaseDate)}
                           </Typography>
                        </Box>
                     </TicketInfo>

                     <OrgInfoContainer>
                        <OrgAvatar>
                           <Box
                              component='img'
                              src={selectedTicket.organizationLogo}
                              alt={selectedTicket.organizationName}
                              sx={{ width: 40, height: 40, mr: 2, borderRadius: '50%' }}
                           />
                           <Typography variant='h6'>{selectedTicket.organizationName}</Typography>
                        </OrgAvatar>

                        <Box sx={{ display: 'flex', mt: 2, gap: 1 }}>
                           <Button
                              variant='outlined'
                              startIcon={<BusinessIcon />}
                              component={Link}
                              to={`/organizations/${selectedTicket.organizationId}/profile`}
                              onClick={handleCloseQrDialog}
                              size='small'
                              sx={{ flex: 1 }}
                           >
                              View Organization
                           </Button>

                           <Button
                              variant='outlined'
                              startIcon={<EventIcon />}
                              component={Link}
                              to={`/explore-events/?organizationId=${selectedTicket.organizationId}`}
                              onClick={handleCloseQrDialog}
                              size='small'
                              sx={{ flex: 1 }}
                           >
                              More Events
                           </Button>
                        </Box>
                     </OrgInfoContainer>

                     <Typography
                        variant='caption'
                        color='textSecondary'
                        sx={{ display: 'block', mt: 2, textAlign: 'center' }}
                     >
                        Present this QR code at the event entrance for scanning
                     </Typography>
                  </>
               )}
            </DialogContent>
         </Dialog>
      </PageContainer>
   );
};

export default MyTicketsPage;
